<template>
  <Dialog
    :model-value="modelValue"
    v-bind="{ ...$attrs }"
    max-width="none"
    persistent
    content-class="submit-order-share-dialog"
    @update:model-value="val => $emit('update:modelValue', val)"
  >
    <template #content>
      <div>
        <div
          class="share-info-text"
          v-html="$t('Confirm share order')"
        />
      </div>
      <div>
        <v-data-table
          :headers="headers"
          :items="selectedUsers"
          :loading="null"
          :loading-text="$t('Loading Users')"
          hide-default-footer
          :items-per-page="-1"
          :no-data-text="$t('Don\'t share with anyone')"
          mobile-breakpoint="-1"
        />
      </div>
      <div class="action-buttons">
        <Button
          color="secondary"
          class="text-primary"
          @click="$emit('cancel')"
        >
          {{ $t("Go back & adjust Sharing") }}
        </Button>
        <Button
          @click="$emit('closePopup')"
        >
          {{ $t("Confirm & Finish") }}
        </Button>
      </div>
    </template>
    <template #actions>
      &nbsp;
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { User } from '@/entities/user.ts'
import { useI18n } from "vue-i18n"
import Dialog from "@/components/dialogs/Dialog";
import Button from "@/components/input-components/Button.vue";
import { useAuth } from "@/composables/useAuth";

defineOptions({ name: "SubmitOrderShareDialog" });

const { t } = useI18n()
const { user } = useAuth()

interface SubmitOrderShareDialogProps {
  allSharedUsers: User[]
  selectedUser: string[]
  modelValue: boolean
}

const props = defineProps<SubmitOrderShareDialogProps>()

const emit = defineEmits<{
  cancel: []
  closePopup: []
  "update:modelValue": [value: boolean]
}>()

const headers = computed(() => [
  { title: "", value: "controls", sortable: false },
  { title: t("Salutation"), value: "salutation", sortable: false },
  { title: t("Title"), value: "title", sortable: false },
  { title: t("First name"), value: "firstName", sortable: false },
  { title: t("Last name"), value: "lastName", sortable: false },
  { title: t("Email"), value: "email", sortable: false },
])

const selectedUsers = computed((): User[] =>
  props.allSharedUsers
    .filter(
      (u) =>
        props.selectedUser.find(
          (u2) => u2 === u.email || u2 === u.doctorEmail
        )
    )
    .map((user) => ({
      salutation: user.doctorSalutation ?? user.salutation,
      title: user.doctorTitle ?? user.title,
      firstName: user.doctorFirstName ?? user.firstName,
      lastName: user.doctorLastName ?? user.lastName,
      email: user.doctorEmail ?? user.email,
    }))
)
</script>

<style lang="scss">
.submit-order-share-dialog {
  margin: 0;

  &__item {
    display: flex;
    justify-content: space-between;
    padding-top: auto;
    padding-bottom: auto;

    &__item--long {
      min-width: 100px;
      overflow-wrap: break-word;
    }

    &__item--short {
      margin-right: 2rem;
      margin-left: 1rem;
    }

    &__item {
      margin-top: auto;
      margin-bottom: auto;
    }
  }

  .sub-title {
    margin: 0.625rem 0;
    font-weight: bold;
  }

  .share-info-text {
    font-size: .875rem;
    margin-bottom: 2rem;
  }

  .action-buttons {
    margin-top: 1.5rem;

    > button {
      display: block;
      width: 100%;

      &:first-child {
        margin-bottom: 1rem;
      }
    }

    @media #{map.get($display-breakpoints, 'sm-and-up')} {
      display: flex;

      > button {
        flex: 1;
        margin-left: .5rem;

        &:first-child {
          margin-left: 0;
          margin-right: .5rem;
        }
      }
    }
  }
}
</style>
